import { CURATED_TIMEZONES, getTimezoneDisplayName } from '@libs/shared/common/helpers/timezone.helper';
import * as ct from 'countries-and-timezones';

import { DEFAULT_TIMEZONE, LOCALE } from '@/constants/locale';

interface TimezoneItem {
  /** Unique key for react-aria collection (the display name, e.g. "Eastern Time - US & Canada"). */
  id: string;
  /** IANA timezone identifier to submit to the backend (e.g. "America/New_York"). */
  iana: string;
  /** Full label with current time (e.g. "Eastern Time - US & Canada (4:48pm)"). */
  label: string;
  /** Current time in this timezone (e.g. "4:48pm"). */
  time: string;
}

/**
 * All timezones including deprecated aliases (legacy name → canonical).
 * Used by normalize() to remap stored legacy names (e.g. Europe/Kiev → Europe/Kyiv).
 */
const ALL_TIMEZONES = ct.getAllTimezones({ deprecated: true });

let currentTimezone: string = DEFAULT_TIMEZONE;

function getTime (timeZone: string, date: Date): string {
  const formatted = new Intl.DateTimeFormat(LOCALE, {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);

  // Normalize "4:48 PM" → "4:48pm"
  return formatted.replace(/\s?(AM|PM)/i, (_, meridiem: string) => meridiem.toLowerCase());
}

export const timezone = {
  /**
   * Returns the canonical IANA timezone name accepted by the backend.
   * Remaps legacy IANA aliases (e.g. Europe/Kiev → Europe/Kyiv) via the
   * countries-and-timezones aliasOf field. Falls back to DEFAULT_TIMEZONE
   * if the name is entirely unrecognised.
   */
  normalize: (tz: string): string => {
    const entry = ALL_TIMEZONES[tz as keyof typeof ALL_TIMEZONES];
    return entry?.aliasOf ?? entry?.name ?? DEFAULT_TIMEZONE;
  },

  get: (): string => currentTimezone,

  set: (tz: string): void => {
    currentTimezone = timezone.normalize(tz);
  },

  list: (): TimezoneItem[] => {
    const now = new Date();
    return CURATED_TIMEZONES.map(({ name, iana }) => {
      const time = getTime(iana, now);
      return { id: name, iana, label: `${name} (${time})`, time };
    });
  },

  /** Given a display-name key (ListBoxItem id), returns the IANA identifier for the backend. */
  getIana: (displayKey: string): string => {
    const match = CURATED_TIMEZONES.find((item) => item.name === displayKey);
    return match?.iana ?? displayKey;
  },

  /** Given a stored IANA id, returns the matching display-name key for the ComboBox selectedKey. */
  getKey: (ianaId: string): string | null => {
    // Normalize first so aliases like 'UTC' → 'Etc/UTC' resolve to their curated entry.
    const canonical = timezone.normalize(ianaId);
    const name = getTimezoneDisplayName(canonical);
    // getTimezoneDisplayName falls back to the raw IANA string when not in the curated list,
    // so check whether the result differs from the canonical id to detect a real match.
    return name !== canonical ? name : null;
  },

  /**
   * Returns the ComboBox selectedKey for a stored IANA timezone.
   * Falls back to offset-based curated matching when there is no exact curated entry.
   */
  getSelectedKey: (ianaId: string): string | null => timezone.getKey(ianaId) ?? timezone.findCurated(ianaId)?.id ?? null,

  /**
   * Finds the best-matching curated TimezoneItem for any IANA identifier,
   * including ones not directly in the curated list (e.g. Europe/Kyiv).
   * First tries an exact iana match, then falls back to the curated entry
   * with the same current UTC offset.
   * Returns null if no match is found (e.g. invalid zone).
   */
  findCurated: (ianaId: string): TimezoneItem | null => {
    const items = timezone.list();

    // Exact match first
    const exact = items.find((item) => item.iana === ianaId);
    if (exact) {
      return exact;
    }

    // Metadata-based fallback using countries-and-timezones static utcOffset + dstOffset.
    // The IANA continent prefix (e.g. "Europe", "Asia") is used as a geographic
    // tiebreaker so that Europe/Kyiv matches a European curated zone rather than
    // Africa/Cairo even though both share UTC+2 standard / UTC+3 DST.
    const tz = ALL_TIMEZONES[ianaId as keyof typeof ALL_TIMEZONES];
    if (!tz) {
      return null;
    }

    const continent = ianaId.split('/')[0];

    // Same continent + same standard and DST offsets
    const sameContinent = items.find((item) => {
      const ctz = ALL_TIMEZONES[item.iana as keyof typeof ALL_TIMEZONES];
      return ctz &&
        item.iana.split('/')[0] === continent &&
        ctz.utcOffset === tz.utcOffset &&
        ctz.dstOffset === tz.dstOffset;
    });
    if (sameContinent) {
      return sameContinent;
    }

    // Any curated zone with matching offsets
    return items.find((item) => {
      const ctz = ALL_TIMEZONES[item.iana as keyof typeof ALL_TIMEZONES];
      return ctz && ctz.utcOffset === tz.utcOffset && ctz.dstOffset === tz.dstOffset;
    }) ?? null;
  },
};
